import * as React from "react"
import { type ButtonVariant } from "@digits-shared/DesignSystem/Button"
import { useDefinedConstant } from "@digits-shared/hooks/useConstant"

export interface GsiRenderOptions {
  signingIn: boolean
  setSigningIn: React.Dispatch<React.SetStateAction<boolean>>
  render: (props: {
    onClick: () => void
    disabled?: boolean
    text?: string
    variant?: ButtonVariant
    loading?: boolean
  }) => React.JSX.Element
  disabled?: boolean
  variant?: ButtonVariant
  loading?: boolean
}

export interface GisLoginOptions {
  onSuccess?: (response: { code: string }) => void
  onFailure?: (error: { error: string; type?: string }) => void
}

export const GISLogin = (props: GsiRenderOptions & GisLoginOptions) => {
  const { render, onSuccess, onFailure, signingIn, setSigningIn, disabled, variant, loading } =
    props

  const [scriptLoaded, setScriptLoaded] = React.useState(false)

  // load the Google Identity Services script
  React.useEffect(() => {
    const scriptTag = document.createElement("script")
    scriptTag.src = "https://accounts.google.com/gsi/client"
    scriptTag.async = true
    scriptTag.defer = true
    scriptTag.onload = () => {
      setScriptLoaded(true)
    }
    scriptTag.onerror = () => {
      TrackJS?.track("Unable to load Google Identity Services login script")
      setScriptLoaded(false)
    }

    document.body.appendChild(scriptTag)
  }, [])

  const client = useDefinedConstant(() => {
    if (!scriptLoaded) return undefined

    // using window.google instead of the google.accounts namespace
    // because include_granted_scopes isn't defined within the named types package
    return window.google.accounts.oauth2.initCodeClient({
      client_id: process.env.GOOGLE_OAUTH_CLIENT_ID || "",
      scope: process.env.GOOGLE_OAUTH_SCOPE || "",
      include_granted_scopes: false,
      ux_mode: "popup",
      callback: (response: google.accounts.oauth2.CodeResponse) => {
        setSigningIn(false)
        response.error ? onFailure?.(response) : onSuccess?.(response)
      },
      // non oauth related errors
      error_callback: (configError: google.accounts.oauth2.ClientConfigError) => {
        const { message: error, type } = configError
        setSigningIn(false)
        onFailure?.({ error, type })
      },
    })
  })

  const onClick = React.useCallback(() => {
    setSigningIn(true)
    return client?.requestCode()
  }, [client, setSigningIn])

  const isDisabled = disabled || !client || signingIn

  return render({
    onClick,
    disabled: isDisabled,
    text: "Continue with Google",
    variant,
    loading,
  })
}
